import React, { useEffect, useMemo, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from 'formik';
import AttachFileIcon from "@mui/icons-material/AttachFile";
import * as Yup from 'yup';
import { useDispatch, useSelector } from "react-redux";
import { submitMaterialQuotation } from "actions/vendorQuotationAction";
import { useSnackbar } from 'notistack';
import { useLocation, useNavigate } from "react-router-dom";
import StaticPopup from "components/StaticPopup";
import FileUpload from "./FileUpload";
import { vendorQuotationService } from "services/vendorQuotationService";
import LoadingModal from "views/Rfqs/LoadingModal";
import { handleDownloadExcel, handleUploadExcel } from './excelHelpers';
import {
    handleMaterialFileUploadClick,
    handleMaterialFileUpload,
    handleDeleteMaterialFile,
    handleCloseMaterialFileSizeError
} from './materialFileUpload';
import ReactHtmlParser from "react-html-parser";
import RfqInformation from "./RfqInformation";
import { getFormattedUnit } from "config";
import MaterialMobileReadOnly from "./MaterialMobileReadOnly";
import BookmarkIcon from '@mui/icons-material/Bookmark';
import QuotationDesktopStyles from "./QuotationDesktopStyles";
import { shouldShowVendorRank } from "./utils/rankVisibility";
import QuotationTopNav from "./QuotationTopNav";

const MaterialQuotationReadOnly = () => {

    const { loading, error, errorMessage, quotation } = useSelector((state) => state.vendorQuotation);
    const { enqueueSnackbar } = useSnackbar();

    const auth = useSelector((state) => state.auth);
    const userProfile = auth.user;

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const queryRfqId = searchParams.get("rfqId");
    const getStoredRfqData = () => {
        if (!queryRfqId) return {};

        try {
            return JSON.parse(sessionStorage.getItem(`rfq-quotation-view-${queryRfqId}`) || "{}")?.rfqData || {};
        } catch (error) {
            console.error("Failed to read stored RFQ data:", error);
            return {};
        }
    };
    const [rfqData, setRfqData] = useState(location.state?.rfqData || getStoredRfqData() || {});
    const { isEdit } = location.state || {};
    const { otherCharges } = rfqData || {};

    const supplierId = location.state?.supplierId || searchParams.get("supplierId") || "";
    const queryQuotationIndex = searchParams.get("quotationIndex");
    const quotationIndex = location.state?.quotationIndex ?? (queryQuotationIndex !== null ? Number(queryQuotationIndex) : undefined);
    const isReadOnly = quotationIndex !== undefined || searchParams.get("readOnly") === "true" || rfqData?.status === "CLOSED"
    const mostRecentquotation = quotationIndex !== undefined ? rfqData?.suppliers?.[0]?.vendorQuotations?.quotationSets?.[quotationIndex] : rfqData?.suppliers?.[0]?.vendorQuotations?.quotationSets?.slice(-1)[0]
    // console.log("mostRecentquotation", mostRecentquotation)

    useEffect(() => {
        const fetchVendorQuotation = async () => {
            if (supplierId) {
                try {
                    const payload = { rfqId: rfqData?.rfqId, supplierId };
                    const response = await vendorQuotationService.getVendorQuotation(payload);
                    // console.log("Fetched Vendor Quotation Data:", response);

                    // Update rfqData.suppliers[0] with fetched vendorQuotations
                    if (response?.vendorQuotations) {
                        setRfqData(prevData => ({
                            ...prevData,
                            suppliers: prevData.suppliers?.map((supplier, index) =>
                                index === 0 ? { ...supplier, vendorQuotations: response.vendorQuotations } : supplier
                            ) || []
                        }));
                    }
                } catch (error) {
                    console.error("Error fetching vendor quotation:", error);
                }
            }
        };

        fetchVendorQuotation();
    }, [supplierId, isEdit, rfqData?.rfqId]);

    useEffect(() => {
        if (rfqData?.suppliers?.[0]?.vendorQuotations) {

            const latestQuotationSet = quotationIndex !== undefined ? rfqData?.suppliers?.[0]?.vendorQuotations?.quotationSets?.[quotationIndex] : rfqData?.suppliers?.[0]?.vendorQuotations?.quotationSets?.slice(-1)[0];

            setTotalCosts(latestQuotationSet?.materialDetails?.map((material) => material?.netPrice) || rfqData?.materials.map(() => 0));
            setTotalMaterialCostsExclGST(latestQuotationSet?.totalWithoutGst > 0 ? latestQuotationSet?.totalWithoutGst : 0)
            setOverallTotal(quotationIndex !== undefined ? (latestQuotationSet?.othersTotalWithGst + latestQuotationSet?.totalWithGst) : rfqData.suppliers[0].vendorQuotations.grandTotalWithGst || 0);
            setOverallTotalExclGST(latestQuotationSet?.totalWithoutGst + latestQuotationSet?.othersTotalWithoutGst || 0)
            setSelectedTotalWithoutGst(latestQuotationSet?.selectedTotalWithoutGst + latestQuotationSet?.selectedOthersTotalWithoutGst || 0)
            setSelectedTotalWithGst(latestQuotationSet?.selectedTotalWithGst + latestQuotationSet?.selectedOthersTotalWithGst || 0)
            setSelectedFilesBase(latestQuotationSet?.attachments || []);
            setIsCounterTerm(latestQuotationSet?.agreedToPaymentTerms || false);


            const otherCharges = latestQuotationSet?.otherCharges || {};

            setMiscellaneousCosts({
                transport: otherCharges?.transport || { mrp: 0, gst: 0, totalCost: 0 },
                loading: otherCharges?.loading || { mrp: 0, gst: 0, totalCost: 0 },
                unLoading: otherCharges?.unLoading || { mrp: 0, gst: 0, totalCost: 0 },
                mathadeCharges: otherCharges?.mathadeCharges || { mrp: 0, gst: 0, totalCost: 0 },
                miscCharges: otherCharges?.miscCharges || { mrp: 0, gst: 0, totalCost: 0 }
            });
        } else {
            setMiscellaneousCosts({
                transport: { mrp: 0, gst: 0, totalCost: 0 },
                loading: { mrp: 0, gst: 0, totalCost: 0 },
                unLoading: { mrp: 0, gst: 0, totalCost: 0 },
                mathadeCharges: { mrp: 0, gst: 0, totalCost: 0 },
                miscCharges: { mrp: 0, gst: 0, totalCost: 0 }
            });
        }
    }, [rfqData, quotationIndex]);


    const miscellaneousItems = [
        { label: "Transport", name: "transport" },
        { label: "Loading", name: "loading" },
        { label: "Unloading", name: "unLoading" },
        { label: "Mathade Charges", name: "mathadeCharges" },
        { label: "Misc. Charges", name: "miscCharges" },
    ];

    const visibleMiscellaneousItems = miscellaneousItems.filter(item => otherCharges?.[item.name]);
    const isAdvancedTransport = rfqData?.transportOption === "perUnitAmount"


    // Check if any items are visible
    const hasVisibleMiscellaneousItems = visibleMiscellaneousItems.length > 0;

    const currentRank = rfqData?.suppliers[0]?.vendorQuotations?.rank

    const attachmentsData = rfqData?.selectedFilesBase
        ? rfqData.selectedFilesBase.map((item) => {
            const lastIndex = item.Name.lastIndexOf('.'); // Extract file extension
            const path = lastIndex !== -1 ? item.Name.substring(lastIndex + 1) : null;
            return { ...item, Path: path }; // Add the Path to determine file type
        })
        : [];

    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [isSpecsPopupOpen, setIsSpecsPopupOpen] = useState(false);
    const [totalCosts, setTotalCosts] = useState(rfqData?.suppliers?.[0]?.vendorQuotations?.quotationSets?.slice(-1)[0]?.materialDetails?.map((material) => material?.netPrice) || rfqData?.materials.map(() => 0));
    const [overallTotal, setOverallTotal] = useState(rfqData?.suppliers?.[0]?.vendorQuotations?.grandTotalWithGst || 0);
    const [overallTotalExclGST, setOverallTotalExclGST] = useState(0);
    const [selectedTotalWithoutGst, setSelectedTotalWithoutGst] = useState(0);
    const [selectedTotalWithGst, setSelectedTotalWithGst] = useState(0);
    const [totalMaterialCostsExclGST, setTotalMaterialCostsExclGST] = useState(mostRecentquotation?.totalWithoutGst > 0 ? mostRecentquotation?.totalWithoutGst : 0)
    const [miscellaneousCosts, setMiscellaneousCosts] = useState({
        transport: { mrp: 0, gst: 0, totalCost: 0 },
        loading: { mrp: 0, gst: 0, totalCost: 0 },
        unLoading: { mrp: 0, gst: 0, totalCost: 0 },
        mathadeCharges: { mrp: 0, gst: 0, totalCost: 0 },
        miscCharges: { mrp: 0, gst: 0, totalCost: 0 }
    });

    // console.log("selectedTotalWithoutGst", selectedTotalWithoutGst)
    // console.log("selectedTotalWithGst", selectedTotalWithGst)

    const [isCounterTerm, setIsCounterTerm] = useState(false);
    // const [isCounterTerm, setIsCounterTerm] = useState(!rfqData?.suppliers?.[0]?.vendorQuotations?.quotationSets?.slice(-1)[0]?.agreedToPaymentTerms || false);
    const [attachmentsSkeleton, setAttachmentsSkeleton] = useState([]);
    const [selectedFilesBase, setSelectedFilesBase] = useState(rfqData?.suppliers?.[0]?.vendorQuotations?.quotationSets?.slice(-1)[0]?.attachments || []);
    const [selectedFilesTotalSize, setSelectedFilesTotalSize] = useState([]);
    const [attachmentsUploadsErrors, setAttachmentsUploadsErrors] = useState("");
    const [openFilesSizeError, setOpenFilesSizeError] = useState(false);
    const [openAccordian, setOpenAccordian] = useState(null); // State to manage open accordion
    const [openSpecsPopup, setOpenSpecsPopup] = useState(false);
    const [selectedSpecs, setSelectedSpecs] = useState("");

    const [excelLoading, setExcelLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState("");
    const [excelError, setExcelError] = useState(null);


    const [materialFilesBase, setMaterialFilesBase] = useState([]);
    const [materialFilesSkeleton, setMaterialFilesSkeleton] = useState([]);
    const [materialFilesTotalSize, setMaterialFilesTotalSize] = useState([]);
    const [materialUploadsErrors, setMaterialUploadsErrors] = useState("");
    const [openMaterialFileSizeError, setOpenMaterialFileSizeError] = useState(false);
    const [openUploadFile, setOpenUploadFile] = useState(false);
    const [selectedMaterialIndex, setSelectedMaterialIndex] = useState(null);
    const [expandedStates, setExpandedStates] = useState({}); // Track expanded states for each item
    const toggleExpanded = (index) => {
        setExpandedStates((prev) => ({
            ...prev,
            [index]: !prev[index], // Toggle specific item's state
        }));
    };

    const getInitialValues = (rfqData, userProfile) => {
        const isEditing = Boolean(rfqData?.suppliers?.[0]?.vendorQuotations);
        const lastQuotationSet = isEditing
            ? (quotationIndex !== undefined
                ? rfqData?.suppliers?.[0]?.vendorQuotations?.quotationSets?.[quotationIndex]
                : rfqData?.suppliers?.[0]?.vendorQuotations?.quotationSets?.slice(-1)[0])
            : null;

        // Create a map of materials from lastQuotationSet for quick lookup by ID and name
        const quotationMaterialMap = (lastQuotationSet?.materialDetails || []).reduce((map, material) => {
            if (material.id) {
                map.byId[material.id.toString()] = material;
            }
            if (material.name) {
                map.byName[material.name.toLowerCase()] = material;
            }
            return map;
        }, { byId: {}, byName: {} });

        // Merge rfqData.materials with lastQuotationSet.materialDetails
        const mergedMaterials = (rfqData?.materials || []).map(material => {
            const matchedMaterial =
                quotationMaterialMap.byId[material._id?.toString()] || // Match by ID
                quotationMaterialMap.byName[material.name?.toLowerCase()]; // Fallback to name match

            return matchedMaterial
                ? {
                    name: matchedMaterial.name,
                    quantity: material.quantity, // Use updated quantity from rfqData
                    unit: matchedMaterial.unit || material.unit,
                    id: matchedMaterial.id, // Retain ID from lastQuotationSet
                    specifications: matchedMaterial.specifications || material.specifications,
                    brand: matchedMaterial.brand,
                    basePrice: matchedMaterial.basePrice || 0,
                    transportRate: matchedMaterial.transportRate || 0,
                    transportCost: matchedMaterial.transportCost || 0,
                    discount: matchedMaterial.discount || 0,
                    gst: matchedMaterial.gst,
                    totalCost: matchedMaterial.totalCost || 0,
                    totalExclGst: matchedMaterial.priceWithoutGst || 0,
                    netPrice: matchedMaterial.netPrice || 0,
                    remark: matchedMaterial.remark || "",
                    deliveryDate: matchedMaterial?.deliveryDate
                        ? new Date(matchedMaterial.deliveryDate).toISOString().split("T")[0]
                        : "",
                    attachments: matchedMaterial.attachments || [],
                    isSelected: matchedMaterial.isSelected || false,
                    selectedDiscount: matchedMaterial.selectedDiscount,
                    selectedItemRate: matchedMaterial.selectedItemRate,
                    selectedTransportCost: matchedMaterial.selectedTransportCost,
                    selectedTransportRate: matchedMaterial.selectedTransportRate,
                    selectedSupplierPrice: matchedMaterial.selectedSupplierPrice,
                }
                : {
                    name: material.name,
                    quantity: material.quantity,
                    unit: material.unit,
                    id: material._id, // Use the ID from rfqData
                    specifications: material.specifications,
                    brand: material.brands?.join(", ") || "",
                    basePrice: "",
                    transportRate: 0,
                    transportCost: 0,
                    discount: 0,
                    gst: 18,
                    totalCost: 0,
                    totalExclGst: 0,
                    netPrice: 0,
                    remark: "",
                    deliveryDate: rfqData?.providingDate
                        ? new Date(rfqData.providingDate).toISOString().split("T")[0]
                        : "",
                    attachments: []
                };
        });

        return {
            materials: mergedMaterials,

            miscellaneousCosts: {
                transport: {
                    mrp: isEditing ? lastQuotationSet?.otherCharges?.transport?.mrp || 0 : 0,
                    selectedMrp: isEditing ? lastQuotationSet?.otherCharges?.transport?.selectedMrp || 0 : 0,
                    gst: isEditing ? lastQuotationSet?.otherCharges?.transport?.gst || 18 : 18,
                    totalCost: isEditing ? lastQuotationSet?.otherCharges?.transport?.totalCost || 0 : 0,
                    remark: isEditing ? lastQuotationSet?.otherCharges?.transport?.remark || "" : ""
                },
                loading: {
                    mrp: isEditing ? lastQuotationSet?.otherCharges?.loading?.mrp || 0 : 0,
                    selectedMrp: isEditing ? lastQuotationSet?.otherCharges?.loading?.selectedMrp || 0 : 0,
                    gst: isEditing ? lastQuotationSet?.otherCharges?.loading?.gst || 18 : 18,
                    totalCost: isEditing ? lastQuotationSet?.otherCharges?.loading?.totalCost || 0 : 0,
                    remark: isEditing ? lastQuotationSet?.otherCharges?.loading?.remark || "" : ""
                },
                unLoading: {
                    mrp: isEditing ? lastQuotationSet?.otherCharges?.unLoading?.mrp || 0 : 0,
                    selectedMrp: isEditing ? lastQuotationSet?.otherCharges?.unLoading?.selectedMrp || 0 : 0,
                    gst: isEditing ? lastQuotationSet?.otherCharges?.unLoading?.gst || 18 : 18,
                    totalCost: isEditing ? lastQuotationSet?.otherCharges?.unLoading?.totalCost || 0 : 0,
                    remark: isEditing ? lastQuotationSet?.otherCharges?.unLoading?.remark || "" : ""
                },
                mathadeCharges: {
                    mrp: isEditing ? lastQuotationSet?.otherCharges?.mathadeCharges?.mrp || 0 : 0,
                    selectedMrp: isEditing ? lastQuotationSet?.otherCharges?.mathadeCharges?.selectedMrp || 0 : 0,
                    gst: isEditing ? lastQuotationSet?.otherCharges?.mathadeCharges?.gst || 18 : 18,
                    totalCost: isEditing ? lastQuotationSet?.otherCharges?.mathadeCharges?.totalCost || 0 : 0,
                    remark: isEditing ? lastQuotationSet?.otherCharges?.mathadeCharges?.remark || "" : ""
                },
                miscCharges: {
                    mrp: isEditing ? lastQuotationSet?.otherCharges?.miscCharges?.mrp || 0 : 0,
                    selectedMrp: isEditing ? lastQuotationSet?.otherCharges?.miscCharges?.selectedMrp || 0 : 0,
                    gst: isEditing ? lastQuotationSet?.otherCharges?.miscCharges?.gst || 18 : 18,
                    totalCost: isEditing ? lastQuotationSet?.otherCharges?.miscCharges?.totalCost || 0 : 0,
                    remark: isEditing ? lastQuotationSet?.otherCharges?.miscCharges?.remark || "" : ""
                }
            },

            agreedToPaymentTerms: isEditing ? lastQuotationSet?.agreedToPaymentTerms : true,
            counterPaymentTerms: isEditing ? lastQuotationSet?.counterPaymentTerms || "" : "",
            notes: isEditing ? lastQuotationSet?.notes : ""
        };
    };

    const toggleAccordion = (index) => {
        setOpenAccordian(openAccordian === index ? null : index); // Toggle the open state
    };

    const togglePopup = () => {
        setIsPopupOpen(!isPopupOpen);
    };
    const togglePopupSpecs = () => {
        setIsSpecsPopupOpen(!isSpecsPopupOpen);
    };

    const handleCloseForm = () => {
        if (supplierId) {
            navigate('/leads')
        } else {
            navigate(-1)
        }
    }

    // Function to open the popup with full specifications
    const openFullSpecs = (specifications) => {
        setSelectedSpecs(specifications); // Set the full specifications
        setOpenSpecsPopup(true); // Open the popup
    };

    // Function to close the popup
    const closeSpecsPopup = () => {
        setOpenSpecsPopup(false); // Close the popup
    };


    // Yup validation schema
    const miscellaneousSchema = Yup.object().shape({
        mrp: Yup.number().typeError("Enter a valid MRP").required("MRP is required").min(0, "Invalid MRP"),
        gst: Yup.number().typeError("Enter a valid GST").required("GST is required"),
        // totalCost: Yup.number().typeError("Total cost should be a number").required(),
    });


    const validationSchema = Yup.object().shape({
        materials: Yup.array().of(
            Yup.object().shape({
                basePrice: Yup.number().typeError("Enter a valid unit cost").required("Required").min(0, "Invalid cost"),
                discount: Yup.number().typeError("Enter a number without %"),
                gst: Yup.string().typeError("Enter a valid GST").required("Required").min(0, "Invalid GST"),
            })
        ),
    });

    const [currentStep, setCurrentStep] = useState(0);

    const steps = [
        "rfq info",
        "Input Fields",
        "Miscellaneous",
        "Supporting Documents",
        "Additional Information & Upload",
        "Completion"
    ];

    const handleNextStep = async (validateForm, setTouched, values) => {
        const errors = await validateForm(); // Trigger Formik validation for current step
        // Find the index of the first material with an error
        const errorIndex = errors.materials?.findIndex(error => error != null); // Check for the first non-null error object

        if (errorIndex !== -1) {
            // Set the accordion to open at the first error index
            setOpenAccordian(errorIndex);
            setTouched({
                materials: values.materials.map((material, index) =>
                    index === errorIndex ? { ...material, ...errors.materials[errorIndex] } : material
                ),
            });
        }
        const hasErrors = Object.keys(errors).length > 0; // Check if there are any errors
        if (!hasErrors) {
            // If no errors, move to the next step
            setCurrentStep((prevStep) => prevStep + 1);
        }
    };


    const handlePreviousStep = () => {
        setCurrentStep((prevStep) => prevStep - 1);
    };

    const handleMaterialFileUploadHandler = async (event, materialIndex, setFieldValue, values) => {
        await handleMaterialFileUpload(
            event,
            materialIndex,
            setFieldValue,
            values,
            setMaterialFilesSkeleton,
            setMaterialUploadsErrors,
            setOpenMaterialFileSizeError,
            materialFilesTotalSize
        );
    };

    const handleDeleteMaterialFileHandler = (name, event, selectedMaterialIndex, setFieldValue, values) => {
        handleDeleteMaterialFile(
            name,
            event,
            setFieldValue,
            selectedMaterialIndex,
            values,
            setMaterialFilesBase,
            setMaterialFilesTotalSize
        );
    };

    const handleDownloadMaterialExcel = async () => {
        await handleDownloadExcel(
            rfqData,
            userProfile,
            getInitialValues,
            setExcelLoading,
            setLoadingMessage,
            isAdvancedTransport
        );
    };


    return (
        <div className="w-full h-full bg-white shadow-md" style={{ fontFamily: "goldman_sans" }}>

            <Formik
                enableReinitialize={true}
                initialValues={useMemo(() => getInitialValues(rfqData, userProfile), [rfqData, userProfile])}
                validationSchema={validationSchema}
                onSubmit={async (values) => {
                    const materialDetails = values.materials.map((item, index) => ({
                        id: item.id,
                        name: item.name,
                        brand: item.brand,
                        quantity: parseFloat(item.quantity),
                        basePrice: item.basePrice ? parseFloat(item.basePrice.toString().replace('Rs. ', '')) : 0,
                        transportRate: item.transportRate,
                        discount: item.discount,
                        gst: item.gst,
                        totalCost: totalCosts[index],
                        remark: item.remark, // Add remark to submission
                        deliveryDate: item.deliveryDate, // Add deliveryDate to submission
                        attachments: item.attachments, // Add attachments to
                    }));
                    // Include miscellaneous costs in the submission
                    const submissionData = {
                        materialDetails,
                        otherCharges: values.miscellaneousCosts,
                        agreedToPaymentTerms: values.agreedToPaymentTerms,
                        counterPaymentTerms: values.counterPaymentTerms,
                        notes: values.notes,
                        selectedFilesBase: selectedFilesBase,
                        rfqId: rfqData?.rfqId,
                        rfqType: rfqData?.rfqType,
                        supplierId: rfqData?.suppliers?.[0]?.supplierId,
                        submittedBy: userProfile?.name,
                        userId: userProfile?.userId,
                        isAdvancedTransport
                    };

                    try {
                        await dispatch(submitMaterialQuotation({ values: submissionData }));
                        if (supplierId) {
                            navigate('/leads')
                        } else {
                            navigate(-1)
                        }
                        // if (isEdit) {
                        //     navigate(-1)
                        // } else {
                        //     navigate('/submitted-quotation')
                        // }
                    } catch (error) {
                        console.log("SUBMIT Error", error)
                    }

                }}
            >
                {({ values, errors, touched, setFieldValue, validateForm, setTouched }) => (
                    <Form autoComplete="off" onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}>
                        <QuotationTopNav showRank={shouldShowVendorRank(rfqData)} currentRank={currentRank} />
                        {/*Mobile View */}
                        <MaterialMobileReadOnly
                            values={values}
                            setFieldValue={setFieldValue}
                            currentStep={currentStep}
                            steps={steps}
                            openAccordian={openAccordian}
                            toggleAccordion={toggleAccordion}
                            handleDownloadMaterialExcel={handleDownloadMaterialExcel}
                            rfqData={rfqData}
                            toggleExpanded={toggleExpanded}
                            expandedStates={expandedStates}
                            overallTotal={overallTotal}
                            overallTotalExclGST={overallTotalExclGST}
                            visibleMiscellaneousItems={visibleMiscellaneousItems}
                            attachmentsData={attachmentsData}
                            togglePopup={togglePopup}
                            isPopupOpen={isPopupOpen}
                            isAdvancedTransport={isAdvancedTransport}
                        />


                        {/* desktop view */}
                        <div className="hidden lg:block quotation-desktop-surface">
                            <QuotationDesktopStyles />
                            {false && shouldShowVendorRank(rfqData) && <div className="flex items-center justify-end px-8 pt-4">
                                <div className="flex items-center justify-center space-x-2">
                                    <span class="relative flex items-center justify-center h-4 w-4 ">
                                        <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-lime-500 opacity-40"></span>
                                        <span class="relative inline-flex rounded-full h-2 w-2 bg-lime-500"></span>
                                    </span>
                                    <h3 className="text-sm lg:mt-1 text-center text-[#737373]">
                                        Quotation Rank:{" "}
                                        <span className="font-bold text-black">
                                            {currentRank === "L1"
                                                ? "L2"
                                                : currentRank === undefined || currentRank === null
                                                    ? "Quotation not submitted"
                                                    : currentRank}
                                        </span>
                                    </h3>
                                    <span className="bg-[#cac9c9] text-white text-xs h-[12px] w-[12px] pt-1 flex justify-center items-center rounded-full relative group hover:cursor-pointer">
                                        i
                                        <span className="bg-white px-2 absolute -bottom-5 hidden w-max  text-black text-xs  group-hover:block">
                                            {currentRank === undefined || currentRank === null
                                                ? "Quotation rank is available after quotation submission"
                                                : "Based on your submitted quotation"}
                                        </span>
                                    </span>
                                    {/* <p className="text-[#9F9F9F] lg:mt-1 text-xs italic hidden lg:block">Your rank will update as soon as the buyer receives atleast 3 ratings</p> */}
                                </div>
                            </div>}
                            <div className="bg-transparent">
                                {/* rfq information */}
                                <RfqInformation
                                    rfqData={rfqData}
                                    togglePopup={togglePopup}
                                    isPopupOpen={isPopupOpen}
                                    isAccordion
                                />
                                <div className="lg:px-8 lg:space-y-6 pb-8">


                                    {/* Material Section */}
                                    <div className="rounded-2xl border border-[#E7E7DD] bg-white p-6 shadow-sm">
                                        {/* line items label */}
                                        {/* <h3 className="text-xl pb-2 font-semibold text-[#434343] hidden lg:block">Line Items</h3> */}
                                        {/* material */}
                                        <div className="lg:flex items-center hidden mb-2">
                                            <h3 className="whitespace-nowrap text-[#434343] uppercase text-sm font-semibold">Material List ({rfqData?.materials?.length || 0} {rfqData?.materials?.length === 1 ? "item" : "items"})
                                            </h3>
                                            <hr className="flex-grow border-t border-gray-300 mx-4" />
                                           
                                        </div>

                                        <div className="hidden lg:grid lg:grid-cols-40 gap-1 self-start text-[#777777] text-sm font-normal border-y py-2">
                                            <label className="block font-medium col-span-1 text-xs self-start">Sr#</label>
                                            <label className="block font-medium col-span-4 text-xs self-start">Material</label>
                                            <label className="block font-medium col-span-3 text-xs self-start">Brands</label>
                                            <label className="block font-medium col-span-3 text-xs self-start">Qty</label>
                                            <label className={`block font-medium ${isAdvancedTransport ? "col-span-5" : "col-span-7"} text-xs self-start`}>Specifications</label>
                                            <label className="font-medium text-xs col-span-2 self-start items-center justify-center relative">
                                                <p className="flex items-center"> MRP
                                                    <span className="bg-[#cac9c9] text-white ml-1 text-[10px] h-[10px] w-[10px] flex justify-center items-center mb-1 pt-1 rounded-full lowercase relative group hover:cursor-pointer">
                                                        i
                                                        <span className="absolute bottom-[130%] text-xs left-1/2 w-[200px] transform -translate-x-1/2 px-2 py-1 text-[#434343] text-left bg-white border border-gray-300 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                                                            Enter the cost per unit for the selected construction material. Ensure accuracy as this value impacts overall project budgeting and pricing.
                                                        </span>
                                                    </span>
                                                </p>
                                                Per Unit
                                            </label>
                                            <label className="font-medium text-xs col-span-2 text-right self-start items-center flex relative">
                                                Disc(%)
                                                <span className="bg-[#cac9c9] text-white text-[10px] ml-[2px] h-[10px] w-[10px] flex justify-center items-center mb-1 pt-1 rounded-full lowercase relative group hover:cursor-pointer">
                                                    i
                                                    <span className="absolute bottom-[130%] text-xs left-1/2 w-[220px] transform -translate-x-1/2 px-2 py-1 text-[#434343] text-left bg-white border border-gray-300 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                                                        Specify the discount percentage or amount offered on this material. This discount will be applied to the unit cost and reflected in the final pricing
                                                    </span>
                                                </span>
                                            </label>
                                            <label className="block font-medium text-center col-span-2 self-start text-xs">GST(%)</label>
                                            {isAdvancedTransport && <label className="block font-medium col-span-2 text-center self-start text-xs">Transport Per Unit</label>}
                                            <label className="flex font-medium text-right col-span-2 text-xs">Item Rate
                                                <span className="bg-[#cac9c9] m-1 text-white text-[10px] h-[10px] w-[10px] flex justify-center items-center mb-1 pt-1 rounded-full relative group hover:cursor-pointer">
                                                    i
                                                    <span className="absolute bottom-[130%] text-xs left-1/2 whitespace-nowrap  transform -translate-x-1/2 px-2 py-1 text-[#434343] text-left bg-white border border-gray-300 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                                                        ( MRP - Disc )</span>
                                                </span>
                                            </label>
                                            <label className="block font-medium col-span-3 self-start text-right text-xs">Material <p className="mx-auto pr-2">Cost</p> </label>
                                            {isAdvancedTransport && <label className="block font-medium col-span-2 self-start text-right text-xs">Transport <p className="mx-auto pr-2">Cost</p></label>}
                                            <label className="block font-medium col-span-3 self-start text-right text-xs">Total<br /> (excl.GST)</label>
                                            <label className={`block font-medium ${isAdvancedTransport ? "col-span-4" : "col-span-6"} self-start text-center text-xs`}>Remarks</label>
                                            {/* <label className="block text-center col-span-4 font-medium text-xs">Delivery Date</label> */}
                                            <label className="block font-medium col-span-2 self-start text-center text-xs">Attach.</label>
                                        </div>
                                        <div className="desktop-material-list lg:space-y-1 space-y-6 pt-2 my-2 lg:my-0">
                                            {rfqData?.materials.map((row, index) => (
                                                <div key={index} className="desktop-material-row">
                                                    {/* desktop */}
                                                    <div className="hidden lg:grid lg:grid-cols-40 gap-1 text-[#373737]">

                                                        <p className="text-xs col-span-1 whitespace-pre-line">
                                                        {values?.materials[index]?.isSelected && <BookmarkIcon style={{ fontSize: 12, marginTop:1, color: '#28A745' }} />}
                                                        {index + 1}
                                                        </p>

                                                        <p className="text-xs col-span-4 whitespace-pre-line">{ReactHtmlParser(row.name)}</p>

                                                        <p className="text-xs col-span-3 break-words whitespace-pre-line">{row?.brands.join(", ")}</p>

                                                        <div className="relative group col-span-3">
                                                            <p className="text-xs cursor-pointer mr-1 break-words">
                                                                {row.quantity} {getFormattedUnit(row.unit)}
                                                            </p>
                                                        </div>

                                                        <div className={`${isAdvancedTransport ? "col-span-5" : "col-span-7"}`}>
                                                            {row?.specifications?.length > 0 && (
                                                                <div className="col-span-4 text-xs">
                                                                    <p className="font-light whitespace-pre-line break-words">
                                                                        {ReactHtmlParser(
                                                                            expandedStates[index]
                                                                                ? row?.specifications
                                                                                : row?.specifications?.substring(0, 90) + (row?.specifications?.length > 90 ? "..." : "")
                                                                        )}
                                                                    </p>
                                                                    {row?.specifications?.length > 90 && (
                                                                        <button
                                                                            type="button"
                                                                            className="text-[#8E8E85] text-xs underline italic"
                                                                            onClick={() => toggleExpanded(index)}
                                                                        >
                                                                            {expandedStates[index] ? "Read Less" : "Read More"}
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Base Price - Read Only */}
                                                        <div className="col-span-2 flex flex-col">
                                                            <p className="lg:w-full w-[220px] px-1 text-right text-xs ">
                                                                {values.materials[index]?.basePrice || "0"}
                                                            </p>
                                                        </div>

                                                        {/* Discount - Read Only */}
                                                        <div className="col-span-2 justify-self-center flex flex-col">
                                                            {values.materials[index]?.isSelected && values.materials[index]?.discount !== values.materials[index]?.selectedDiscount ? (
                                                                <>
                                                                    {/* Strike through the original discount */}
                                                                    <p className="w-[40px] px-1 text-xs text-right line-through text-gray-500">
                                                                        {values.materials[index]?.discount ? `${values.materials[index].discount}%` : "-"}
                                                                    </p>
                                                                    {/* Display the selected discount */}
                                                                    <p className="w-[40px] px-1 text-xs text-right ">
                                                                        {values.materials[index]?.selectedDiscount ? `${values.materials[index].selectedDiscount}%` : "-"}
                                                                    </p>
                                                                </>
                                                            ) : (
                                                                // Default display if conditions are not met
                                                                <p className="w-[40px] px-1 text-xs text-right">
                                                                    {values.materials[index]?.discount ? `${values.materials[index].discount}%` : "-"}
                                                                </p>
                                                            )}
                                                        </div>


                                                        {/* GST - Read Only */}
                                                        <div className="col-span-2 w-[50px]">
                                                            <p className="w-full text-xs  text-center">
                                                                {values.materials[index]?.gst ? `${values.materials[index].gst}%` : "-"}
                                                            </p>
                                                        </div>

                                                        {/* Transport Rate - Read Only (Only if Advanced Transport is Enabled) */}
                                                        {isAdvancedTransport && (
                                                            <div className="col-span-2 justify-self-center flex flex-col">
                                                                <p className="w-full px-1 text-xs text-right ">
                                                                    {values.materials[index]?.isSelected && values.materials[index]?.transportRate !== values.materials[index]?.selectedTransportRate ? (
                                                                        <>
                                                                            {/* Strike through the original transportRate */}
                                                                            <span className="line-through text-gray-500">
                                                                                {new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2 }).format(
                                                                                    (values.materials[index]?.transportRate)?.toFixed(0)
                                                                                ) || 0}
                                                                            </span>
                                                                            <br />
                                                                            {/* Display the selectedTransportRate below */}
                                                                            <span className="">
                                                                                {new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2 }).format(
                                                                                    (values.materials[index]?.selectedTransportRate)?.toFixed(0)
                                                                                ) || 0}
                                                                            </span>
                                                                        </>
                                                                    ) : (
                                                                        // Default display if the values match or isSelected is false
                                                                        new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2 }).format(
                                                                            (values.materials[index]?.transportRate)?.toFixed(0)
                                                                        ) || 0
                                                                    )}
                                                                </p>
                                                            </div>
                                                        )}

                                                        <p className="col-span-2 break-words text-right text-xs">
                                                            {values.materials[index]?.isSelected && values.materials[index]?.basePrice &&
                                                                (new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2 }).format(values.materials[index].basePrice - (values.materials[index].basePrice * values.materials[index].discount) / 100) !== values.materials[index]?.selectedItemRate) ? (
                                                                <>
                                                                    {/* Strike through the original calculated value */}
                                                                    <span className="line-through text-gray-500">
                                                                        {new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2 }).format(values.materials[index].basePrice - (values.materials[index].basePrice * values.materials[index].discount) / 100) || 0}
                                                                    </span>
                                                                    <br />
                                                                    {/* Display the selected item rate below */}
                                                                    <span className="">
                                                                        {(values.materials[index]?.selectedItemRate).toFixed(2) || 0}
                                                                    </span>
                                                                </>
                                                            ) : (
                                                                // Default display if conditions are not met
                                                                new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2 }).format(values.materials[index].basePrice - (values.materials[index].basePrice * values.materials[index].discount) / 100) || 0
                                                            )}
                                                        </p>


                                                        <p className="col-span-3 break-words text-right text-xs">
                                                            {values.materials[index]?.isSelected && values.materials[index]?.basePrice &&
                                                                ((values.materials[index].basePrice - (values.materials[index].basePrice * values.materials[index].discount) / 100) * values.materials[index].quantity).toFixed(2) !== (values.materials[index]?.selectedItemRate * values.materials[index]?.quantity).toFixed(2) ? (
                                                                <>
                                                                    {/* Strike through the original calculated value */}
                                                                    <span className="line-through text-gray-500">
                                                                        {new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2 }).format(
                                                                            ((values.materials[index].basePrice - (values.materials[index].basePrice * values.materials[index].discount) / 100) * values.materials[index].quantity).toFixed(2)
                                                                        ) || 0}
                                                                    </span>
                                                                    <br />
                                                                    {/* Display the new calculated value with selected item rate below */}
                                                                    <span className="">
                                                                        {new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2 }).format(
                                                                            (values.materials[index]?.selectedItemRate * values.materials[index]?.quantity).toFixed(2)
                                                                        ) || 0}
                                                                    </span>
                                                                </>
                                                            ) : (
                                                                // Default display if values match or isSelected is false
                                                                new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2 }).format(
                                                                    ((values.materials[index].basePrice - (values.materials[index].basePrice * values.materials[index].discount) / 100) * values.materials[index].quantity).toFixed(2)
                                                                ) || 0
                                                            )}
                                                        </p>


                                                        {isAdvancedTransport && (
                                                            <p className="col-span-2 break-words text-right text-xs">
                                                                {values.materials[index]?.isSelected && values.materials[index]?.transportCost !== values.materials[index]?.selectedTransportCost ? (
                                                                    <>
                                                                        {/* Strike through the original transportCost */}
                                                                        <span className="line-through text-gray-500">
                                                                            {new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2 }).format(
                                                                                (values.materials[index]?.transportCost)?.toFixed(0)
                                                                            ) || 0}
                                                                        </span>
                                                                        <br />
                                                                        {/* Display the selectedTransportCost below */}
                                                                        <span className="">
                                                                            {new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2 }).format(
                                                                                (values.materials[index]?.selectedTransportCost)?.toFixed(0)
                                                                            ) || 0}
                                                                        </span>
                                                                    </>
                                                                ) : (
                                                                    // Default display if the values match or isSelected is false
                                                                    new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2 }).format(
                                                                        (values.materials[index]?.transportCost)?.toFixed(0)
                                                                    ) || 0
                                                                )}
                                                            </p>
                                                        )}


                                                        <p className="break-words col-span-3 text-right text-xs">
                                                            {values.materials[index]?.isSelected && values.materials[index]?.totalExclGst !== values.materials[index]?.selectedSupplierPrice ? (
                                                                <>
                                                                    {/* Strike through the original totalExclGst */}
                                                                    <span className="line-through text-gray-500">
                                                                        {new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2 }).format(
                                                                            (values.materials[index]?.totalExclGst)?.toFixed(0) || 0
                                                                        )}
                                                                    </span>
                                                                    <br />
                                                                    {/* Display the selectedSupplierPrice below */}
                                                                    <span className="">
                                                                        {new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2 }).format(
                                                                            (values.materials[index]?.selectedSupplierPrice)?.toFixed(0) || 0
                                                                        )}
                                                                    </span>
                                                                </>
                                                            ) : (
                                                                // Default display if the values match or isSelected is false
                                                                new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2 }).format(
                                                                    (values.materials[index]?.totalExclGst)?.toFixed(0) || 0
                                                                )
                                                            )}
                                                        </p>


                                                        <div className={`${isAdvancedTransport ? "col-span-5" : "col-span-7"} ml-2 min-h-[30px]`}>
                                                            <style>
                                                                {`
                                                                            /* Remove padding from the ReactQuill editor's container */
                                                                            .no-padding .ql-container {
                                                                                padding: 4px !important;
                                                                                border: none !important;
                                                                            }
                                                                            .no-padding .ql-editor {
                                                                                padding: 4px !important;
                                                                                font-size: 0.75rem !important; /* Equivalent to Tailwind's text-xs */
                                                                                line-height: 1rem; /* Optional: Adjust line height for better readability */
                                                                            }
                                                                            `}
                                                            </style>

                                                            {/* Remark - Read Only */}
                                                            <div className=" text-xs whitespace-normal p-1 w-full no-padding">
                                                                <p className="text-xs">
                                                                    {values?.materials?.[index]?.remark ? (
                                                                        <>
                                                                            <span dangerouslySetInnerHTML={{
                                                                                __html: expandedStates[index]
                                                                                    ? values.materials[index].remark
                                                                                    : values.materials[index].remark.substring(0, 160) +
                                                                                    (values.materials[index].remark.length > 160 ? "..." : "")
                                                                            }} />
                                                                            {values.materials[index].remark.length > 160 && (
                                                                                <button
                                                                                    type="button"
                                                                                    className="text-[#8E8E85] text-xs underline italic ml-1"
                                                                                    onClick={() => toggleExpanded(index)}
                                                                                >
                                                                                    {expandedStates[index] ? "Read Less" : "Read More"}
                                                                                </button>
                                                                            )}
                                                                        </>
                                                                    ) : (
                                                                        " "
                                                                    )}
                                                                </p>
                                                            </div>


                                                            <ErrorMessage name={`materials[${index}].remark`} component="div" className="text-red-500 text-xs" />
                                                        </div>

                                                        {/* Attachment Icon - Disabled */}
                                                        <div className="col-span-1 relative text-center mt-2 opacity-50 cursor-not-allowed">
                                                            <AttachFileIcon
                                                                sx={{
                                                                    width: "0.7em",
                                                                    ml: "2px",
                                                                    transform: "rotate(45deg)",
                                                                    transition: "transform 0.3s ease",
                                                                }}
                                                            />

                                                            {values.materials[index]?.attachments?.length > 0 && (
                                                                <span
                                                                    className="absolute top-0 right-4 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center"
                                                                    style={{ transform: "translate(50%, -50%)" }}
                                                                >
                                                                    {values.materials[index].attachments.length}
                                                                </span>
                                                            )}
                                                        </div>


                                                        {/* <div className="col-span-4 ">
                                                        <Field
                                                            type="date"
                                                            id={`deliveryDate-${index}`}
                                                            name={`materials[${index}].deliveryDate`}
                                                            className={`shadow-sm block text-xs h-[30px] px-2 lg:w-full border border-gray-300 focus:outline-none focus:ring-sky-500 focus:ring-width-1 ${isReadOnly && "bg-gray-100"}`}
                                                            onChange={(e) => handleFieldChange(index, 'deliveryDate', e.target.value, values)} // Ensure you have this function defined
                                                            disabled={isReadOnly && true}
                                                        />
                                                        <ErrorMessage
                                                            name={`materials[${index}].deliveryDate`}
                                                            component="div"
                                                            className="text-red-500 text-xs"
                                                        />
                                                    </div> */}


                                                    </div>

                                                    {index < rfqData?.materials?.length - 1 && <hr className="hidden lg:block  lg:mt-2" />}
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* {hasVisibleMiscellaneousItems && <div className="">
                                        <hr className="hidden lg:block" />
                                        <div className="flex px-4 py-5 lg:p-0 justify-between items-center border lg:border-none border-t-[#007366] bg-[#E0F0EE] lg:bg-white">
                                            <p className="text-[#007366] lg:text-black text-sm px-4">Sub Total</p>
                                            <p className="text-[#007366] text-xs text-end lg:font-normal lg:text-black w-fit min-w-[164px] lg:px-4 py-2">Rs. {new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2 }).format(totalMaterialCostsExclGST.toFixed(2))}</p>
                                        </div>
                                    </div>} */}

                                    {/* Miscellaneous Section */}
                                    <div className="additional-cost-card p-8 bg-white shadow-md">
                                        {hasVisibleMiscellaneousItems && (
                                            <div className="pb-6">
                                                <div className="hidden lg:block">
                                                    <h3 className="text-sm font-semibold text-[#434343] hidden lg:block mb-3">Other Charges</h3>

                                                    <div className="text-xs bg-white border py-4 shadow-md lg:border-none lg:mx-0 lg:shadow-none lg:py-0">
                                                        <div className="mb-3 hidden lg:grid lg:grid-cols-6 items-center gap-2 text-[#777777] font-normal border-y py-2">
                                                            <label className="block font-medium col-span-1">Charges</label>
                                                            <label className="block font-medium text-center col-span-3">Remarks</label>
                                                            <label className="block font-medium text-center col-span-1">GST (%)</label>
                                                            <label className="block font-medium text-center col-span-1">Total Cost</label>
                                                        </div>

                                                        {/* Render visible miscellaneous items */}
                                                        {visibleMiscellaneousItems.map((item) => (
                                                            <div key={item.name} className="lg:grid lg:grid-cols-6 mb-8 lg:mb-0 items-center space-y-1">
                                                                <label className="flex col-span-1 font-semibold lg:font-medium items-center space-x-1">
                                                                    <span>{item.label}</span>
                                                                    {item.name === "transport" && isAdvancedTransport && (
                                                                        <span className="bg-[#cac9c9] text-white text-[12px] h-[14px] w-[14px] flex justify-center items-center rounded-full lowercase relative group hover:cursor-pointer">
                                                                            i
                                                                            <span className="absolute bottom-[130%] left-1/2 w-[200px] transform -translate-x-1/2 px-2 py-1 text-xs text-[#434343] text-left bg-white border border-gray-300 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                                                                                Transport cost based on the per-unit material transport rate
                                                                            </span>
                                                                        </span>
                                                                    )}
                                                                </label>

                                                                {/* Remark - Read Only */}
                                                                <div className="col-span-3 lg:flex hidden justify-center h-fit lg:w-full w-[262px] text-xs p-1">
                                                                    <p className="text-xs">
                                                                        {values?.miscellaneousCosts[item.name]?.remark ? (
                                                                            <span dangerouslySetInnerHTML={{ __html: values.miscellaneousCosts[item.name].remark }} />
                                                                        ) : (
                                                                            " "
                                                                        )}
                                                                    </p>
                                                                </div>

                                                                {/* GST Dropdown - Disabled */}
                                                                <div className="col-span-1 lg:flex hidden justify-center h-fit lg:w-full w-[262px] text-xs p-1 text-center">
                                                                    <p>{values.miscellaneousCosts[item.name]?.gst ?? "-"}</p>
                                                                </div>

                                                                {/* MRP Input Field - Read Only */}
                                                                <div className="col-span-1 w-full">
                                                                    <div className="w-full text-right lg:w-full h-[30px] text-xs placeholder-[#373737] p-2">
                                                                        {values.miscellaneousCosts[item.name]?.mrp !== values.miscellaneousCosts[item.name]?.selectedMrp ? (
                                                                            <div className="flex items-center justify-end">
                                                                                {/* Strike through the original mrp */}
                                                                                <span className="line-through text-gray-500 mr-2">
                                                                                    {new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2 }).format(
                                                                                        (values.miscellaneousCosts[item.name]?.mrp)?.toFixed(0) || 0
                                                                                    )}
                                                                                </span>
                                                                                {/* Display the selectedMrp on the same line */}
                                                                                <span className="">
                                                                                    {new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2 }).format(
                                                                                        (values.miscellaneousCosts[item.name]?.selectedMrp)?.toFixed(0) || 0
                                                                                    )}
                                                                                </span>
                                                                            </div>
                                                                        ) : (
                                                                            // Default display if the values match
                                                                            new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2 }).format(
                                                                                (values.miscellaneousCosts[item.name]?.mrp)?.toFixed(0) || 0
                                                                            )
                                                                        )}
                                                                    </div>
                                                                </div>

                                                            </div>
                                                        ))}



                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Total Amount Section */}
                                        <div className="">
                                            <hr className="hidden lg:block" />
                                            <div className="flex px-4 py-5 lg:p-0 justify-between items-center border lg:border-none border-t-[#007366] bg-[#E0F0EE] lg:bg-white">
                                                <p className="text-[#007366] lg:text-black text-sm">Total Basic Amount</p>
                                                <div className="flex items-center text-[#007366] text-xs text-end lg:font-normal lg:text-black w-fit min-w-[164px] lg:px-4 py-2">
                                                    {overallTotalExclGST !== selectedTotalWithoutGst ? (
                                                        <>
                                                            {/* Strike through the original overallTotalExclGST */}
                                                            <span className="line-through text-gray-500 mr-2">
                                                                Rs. {new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2 }).format(
                                                                    overallTotalExclGST.toFixed(2)
                                                                )}
                                                            </span>
                                                            {/* Display the selectedTotalWithoutGst on the same line */}
                                                            <span className="">
                                                                Rs. {new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2 }).format(
                                                                    selectedTotalWithoutGst.toFixed(2)
                                                                )}
                                                            </span>
                                                        </>
                                                    ) : (
                                                        // Default display if the values match
                                                        `Rs. ${new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2 }).format(
                                                            overallTotalExclGST.toFixed(2)
                                                        )}`
                                                    )}
                                                </div>
                                            </div>
                                        </div>



                                        {/* GST Amount Section */}
                                        <div className="">
                                            <hr className="hidden lg:block" />
                                            <div className="flex px-4 py-5 lg:p-0 justify-between items-center border lg:border-none border-t-[#007366] bg-[#E0F0EE] lg:bg-white">
                                                <p className="text-[#007366] lg:text-black text-sm">GST Total</p>
                                                <div className="flex items-center text-[#007366] text-xs text-end lg:font-normal lg:text-black w-fit min-w-[164px] lg:px-4 py-2">
                                                    {(overallTotal - overallTotalExclGST) !== (selectedTotalWithGst - selectedTotalWithoutGst) ? (
                                                        <>
                                                            {/* Strike through the original GST Total */}
                                                            <span className="line-through text-gray-500 mr-2">
                                                                Rs. {new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2 }).format(
                                                                    (overallTotal - overallTotalExclGST).toFixed(2)
                                                                )}
                                                            </span>
                                                            {/* Display the new GST Total (selectedTotalWithGst - selectedTotalWithoutGst) */}
                                                            <span className="">
                                                                Rs. {new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2 }).format(
                                                                    (selectedTotalWithGst - selectedTotalWithoutGst).toFixed(2)
                                                                )}
                                                            </span>
                                                        </>
                                                    ) : (
                                                        // Default display if the values match
                                                        `Rs. ${new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2 }).format(
                                                            (overallTotal - overallTotalExclGST).toFixed(2)
                                                        )}`
                                                    )}
                                                </div>
                                            </div>
                                        </div>


                                        {/* Total Quotation Value Amount Section */}
                                        <div className="">
                                            <hr className="hidden lg:block" />
                                            <div className="flex px-4 py-5 lg:p-0 justify-between items-center border lg:border-none border-t-[#007366] bg-[#E0F0EE] lg:bg-white">
                                                <p className="text-[#007366] lg:text-black text-sm">Total Quotation Value (inc. GST)</p>
                                                <div className="flex items-center bg-[#E0F0EE] text-[#007366] text-xs text-end lg:font-normal lg:text-black w-fit min-w-[164px] lg:px-4 py-2">
                                                    {overallTotal !== selectedTotalWithGst ? (
                                                        <>
                                                            {/* Strike through the original total */}
                                                            <span className="line-through text-gray-500 mr-2">
                                                                Rs. {new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2 }).format(
                                                                    overallTotal.toFixed(2)
                                                                )}
                                                            </span>
                                                            {/* Display the selectedTotalWithGst on the same line */}
                                                            <span className="">
                                                                Rs. {new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2 }).format(
                                                                    selectedTotalWithGst.toFixed(2)
                                                                )}
                                                            </span>
                                                        </>
                                                    ) : (
                                                        // Default display if the values match
                                                        `Rs. ${new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2 }).format(
                                                            overallTotal.toFixed(2)
                                                        )}`
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                    </div>



                                    {/* Supporting Documents Section */}
                                    {attachmentsData.length > 0 && (
                                        <div className="mb-8 hidden lg:block bg-white p-8 shadow-md">
                                            <>
                                                <h3 className="text-sm font-semibold text-[#434343] hidden lg:block">Supporting Documents</h3>
                                                <p className="text-xs font-normal text-[#9D9D9D] "> <span className="font-bold text-gray-500">Note: </span>  Below are the supporting documents uploaded by the buyer. Please review them.</p>
                                                <div className="lg:grid lg:grid-cols-4 lg:gap-4 space-y-4 lg:space-y-0 lg:mt-4 mx-auto">
                                                    {attachmentsData.map((doc, index) => (
                                                        <div key={index} className="flex justify-between items-center mx-2">
                                                            <div className="px-2 flex w-fit items-center justify-center border border-[#8E8E85] rounded-full py-1 space-x-2 text-[#434343]">
                                                                {/* Dynamic icon based on file type */}
                                                                <img src={`/assets/icons/${doc.Path}.png`} alt={doc.Path} className="h-5 w-5" />
                                                                {/* Document name now clickable */}
                                                                <a
                                                                    href={doc.Content}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    download={doc.Name}
                                                                    className="text-xs text-[#434343] hover:underline"
                                                                >
                                                                    {doc.Name}
                                                                </a>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </>
                                        </div>
                                    )}



                                    {/* Additional Information Section */}
                                    <div className="mb-8 text-sm shadow-md bg-white p-8">

                                        {/* <h3 className="text-sm font-semibold text-[#434343] hidden lg:block ">Additional Information</h3> */}

                                        <div className="hidden space-x-8 lg:flex items-center w-full">
                                            <p className="text-sm">Below are our payment terms. Do you agree?</p>
                                            <div className="flex space-x-4">
                                                <p className="text-sm font-medium">
                                                    {values.agreedToPaymentTerms ? "Yes" : "Counter Offer"}
                                                </p>
                                            </div>
                                        </div>


                                        {isCounterTerm && (
                                            <div className="mt-4 mx-4 lg:mx-0">
                                                <p className="text-sm mb-1 text-[#4F4F42]">Counter Payment Terms</p>
                                                <p className="m-2 w-full break-words text-xs bg-gray-100 p-2">
                                                    {values.counterPaymentTerms || "No counter terms provided."}
                                                </p>
                                            </div>
                                        )}

                                        <div className="mt-4 mx-4 lg:mx-0">
                                            <p className="text-[#434343] lg:mb-0">Make/Brand & Remarks for Client</p>
                                            <p className="m-2 w-full break-words bg-gray-100 text-xs p-2">
                                                {values.notes || "No remarks provided."}
                                            </p>
                                        </div>

                                    </div>

                                    {/* Upload Supporting Documents */}
                                    <div className="hidden lg:block bg-white p-8 shadow-md">
                                        <h3 className="whitespace-nowrap text-[#434343] text-sm font-semibold">Supporting Documents</h3>

                                        <div className="px-3">
                                            {/* Show uploaded documents if available, otherwise display "Documents Not Uploaded" */}
                                            {selectedFilesBase.length > 0 ? (
                                                <ul style={{ display: "flex", flexWrap: "wrap" }}>
                                                    {selectedFilesBase.map((file) => (
                                                        <a
                                                            href={file.Content}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="border border-gray-300 px-3 py-1 rounded-full flex mb-4 ml-10"
                                                            key={file.Name}
                                                        >
                                                            <span className="text-sm overflow-hidden whitespace-nowrap w-36 truncate">
                                                                {file.Name}
                                                            </span>
                                                            <span className="ml-1 cursor-not-allowed opacity-50">
                                                                <svg
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    width="18"
                                                                    height="18"
                                                                    viewBox="0 0 48 48"
                                                                >
                                                                    <circle cx="24" cy="24" r="22" fill="gray" />
                                                                    <line
                                                                        x1="15"
                                                                        y1="15"
                                                                        x2="33"
                                                                        y2="33"
                                                                        stroke="white"
                                                                        strokeWidth="2"
                                                                    />
                                                                    <line
                                                                        x1="33"
                                                                        y1="15"
                                                                        x2="15"
                                                                        y2="33"
                                                                        stroke="white"
                                                                        strokeWidth="2"
                                                                    />
                                                                </svg>
                                                            </span>
                                                        </a>
                                                    ))}
                                                </ul>
                                            ) : (
                                                <p className="text-xs bg-gray-100 mt-2 p-2">Documents Not Uploaded</p>
                                            )}
                                        </div>

                                        {/* Show error messages if any */}
                                        {attachmentsUploadsErrors && (
                                            <div className="mt-3">
                                                <p className="text-red-500 font-semibold text-sm">{attachmentsUploadsErrors}</p>
                                            </div>
                                        )}
                                    </div>

                                </div>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="sticky bottom-0 z-20">
                            {/* action buttons for mobile */}
                            <div
                                style={{ boxShadow: '0 -4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                                className="lg:hidden flex justify-end p-4 space-x-8 bg-white "
                            >
                                {currentStep === 0 ? (
                                    <div className="space-x-4">
                                        {/* {currentStep === 0 && (<div className="lg:flex items-center sticky top-0 shadow-md px-4 py-2 "> */}
                                        {/* <h1 className="text-[24px] text-[#434343] font-semibold ">Add Proposal: Rs. {overallTotal.toFixed(2)}</h1> */}
                                        {/* <span className="text-sm bg-[#E9E9E9] ml-4 px-4 py-2 text-[#434343]">Draft</span> */}
                                        <button type="button" onClick={(() => isEdit ? navigate(-1) : (supplierId ? navigate("/leads") : navigate(-1)))} className="text-xs bg-[#E9E9E9] cursor:pointer px-4 py-2 text-[#434343]">Back</button>

                                        <button
                                            type="button"
                                            className="bg-[#1E1E08] px-4 py-2 text-xs text-white "
                                            onClick={() => setCurrentStep(currentStep + 1)}
                                        >
                                            View Quotation
                                        </button>
                                    </div>
                                ) : (
                                    currentStep > 0 && (
                                        <button
                                            type="button"
                                            className="underline px-4 py-2 text-xs"
                                            onClick={handlePreviousStep}
                                        >
                                            Back
                                        </button>
                                    )
                                )}

                                {/* Show "Next" button for all steps except "Additional Information & Upload" and "Completion" */}
                                {currentStep > 0 && currentStep < steps.length - 2 && (
                                    <button
                                        type="button"
                                        className="bg-[#1E1E08] px-4 py-2 text-xs text-white "
                                        onClick={() => handleNextStep(validateForm, setTouched, values)}
                                    >
                                        Next
                                    </button>
                                )}

                                {/* Show "Submit" button on "Additional Information & Upload" step */}
                                {/* {currentStep === steps.length - 2 && (
                                    <button
                                        type="submit"
                                        className="bg-[#1E1E08] px-4 py-2 text-xs text-white "
                                        disabled={loading}
                                    >
                                        {loading ? "Submitting..." : "Submit"}
                                    </button>
                                )} */}

                                {/* No buttons displayed on "Completion" step */}
                            </div>
                            {/* action buttons for desktop */}
                            {!isReadOnly && <div style={{ boxShadow: '0 -4px 6px -1px rgba(0, 0, 0, 0.1)' }} className="bg-white hidden space-x-4 lg:flex justify-end items-center py-3 lg:px-8 px-4">
                                <button type="button" onClick={handleCloseForm} className="border border-[#1E1E08] px-3 py-2 w-[120px] text-sm text-[#434343] font-bold">
                                    Cancel
                                </button>
                                {/* <button type="submit" disabled={loading} className="bg-black text-sm text-white px-3 py-2 flex justify-center items-center">
                                    {loading ? "Submitting..." : isEdit ? "Update Quotation" : "Submit Quotation"}
                                </button> */}
                            </div>}
                        </div>

                        <StaticPopup
                            openModal={openUploadFile}
                            handleClose={() => setOpenUploadFile(false)}
                            size={"sm"}
                            width={"xl"}
                            bodyStyle="pb-0 pt-0"
                            className="px-0"
                            header={"Upload Supporting Documents"}
                        >
                            <FileUpload
                                handleFileUploadClick={handleMaterialFileUploadClick}
                                handleFileUpload={(event) => handleMaterialFileUploadHandler(event, selectedMaterialIndex, setFieldValue, values)}
                                handleDeleteSelectedFile={handleDeleteMaterialFileHandler}
                                selectedFilesBase={values.materials[selectedMaterialIndex]?.attachments || []} // Get current attachments
                                attachmentsSkeleton={materialFilesSkeleton}
                                attachmentsUploadsErrors={materialUploadsErrors}
                                openFilesSizeError={openMaterialFileSizeError}
                                handleCloseFileSizeError={handleCloseMaterialFileSizeError}
                                selectedMaterialIndex={selectedMaterialIndex}
                                isReadOnly={isReadOnly}
                            />
                        </StaticPopup>
                    </Form>
                )}
            </Formik>


            <LoadingModal
                isOpen={excelLoading || excelError !== null}
                onClose={() => setExcelError(null)} // Close the modal on click
            >
                {excelLoading ? (
                    <div className="flex flex-col items-center justify-center">
                        <div className="animate-spin rounded-full border-4 border-t-4 border-orange-500 border-t-gray-200 h-8 w-8 mb-4"></div>
                        <p className="text-xl font-medium text-gray-600 italic">{loadingMessage}</p>
                    </div>
                ) : (
                    <div>
                        <p className="text-red-500 font-medium">{excelError || "Operation completed successfully!"}</p>
                    </div>
                )}
            </LoadingModal>

            <StaticPopup
                openModal={openSpecsPopup}
                handleClose={closeSpecsPopup}
                width={"xl"}
                bodyStyle="pb-0 pt-0"
                className="px-0"
                header={"Specifications"}
            >
                <div className="py-4">
                    <p>{selectedSpecs}</p> {/* Display the full specifications */}
                </div>
            </StaticPopup>


        </div>
    )
}

export default MaterialQuotationReadOnly
